'use client';

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface ConfirmActionDialogProps {
  isOpen: boolean;
  title: string;
  description: string;
  /** Consequence the user should read before confirming; rendered as an alert above the footer. */
  warning?: string;
  confirmLabel: string;
  /** Label while the action is in flight. */
  pendingLabel: string;
  variant?: 'default' | 'destructive';
  onClose: () => void;
  /**
   * The action itself. Resolving closes the dialog; rejecting keeps it open with the confirm button
   * armed, so the caller's error toast has something to point at and the user can retry.
   */
  onConfirm: () => Promise<void>;
}

/**
 * Confirmation for an action that talks to the API: holds the pending state, closes only on success
 * and refuses to be dismissed mid-flight.
 */
export function ConfirmActionDialog({
  isOpen,
  title,
  description,
  warning,
  confirmLabel,
  pendingLabel,
  variant = 'default',
  onClose,
  onConfirm,
}: ConfirmActionDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm();
    } catch {
      // The caller already reported it. Staying open is the point: closing here would look exactly
      // like a success.
      setIsSubmitting(false);
      return;
    }
    // Before onClose, because the parent usually unmounts this dialog on close.
    setIsSubmitting(false);
    onClose();
  };

  // Escape and the overlay click arrive here too, and neither should abandon a request in flight.
  const handleOpenChange = (open: boolean) => {
    if (!open && !isSubmitting) onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {warning && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{warning}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button
            variant={variant}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? pendingLabel : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
